import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Building2,
  Zap,
  Thermometer,
  Droplets,
  Wind,
  Users,
  Gauge,
  BarChart3,
  TrendingDown,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  AlertTriangle,
  Clock,
  Target,
  Lightbulb,
  Sun,
  Snowflake,
  Activity,
  Shield,
  FileCheck,
  Leaf,
  DollarSign,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

// Custom generated images
const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/973f1235cfd432956d7ba74f7b44fb7e937eccc25cc89a11d3b540a797ffdae0.png",
  dashboard: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/e4d847d84962a06de8f8911f67097987b545599479fdf8b4d390c0ec578f2695.png",
  hvac: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/4fa456f36dc4cd845eb656ee9fb3e8773f2391952b681fe6ffff9f6440b43104.png",
  sensors: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/6c31d7853d4dc7768ea66cee3565dee3da8ca36793c93038fc488db37df33d3d.png",
  occupancy: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/6f483e09e97802f70b3a4b2e6659deaecf1fdf11a55ec8822836a1df06ac501d.png",
  energyFlow: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/298666d857f5bf0f05a986b57a072e21008dfc90035eb8b1f85942f726bc1560.png"
};

const BuildingEnergyEfficiencyPage = () => {
  const { t } = useLanguage();
  
  return (
    <>
      <Helmet>
        <title>{t('buildingEnergyPage.metaTitle')}</title>
        <meta name="description" content={t('buildingEnergyPage.metaDesc')} />
        <meta name="keywords" content="building energy efficiency, energy monitoring buildings, smart building energy management, reduce building energy consumption, building energy optimization, IoT energy monitoring, energy efficiency buildings, smart building sensors, energy management system buildings" />
        <link rel="canonical" href="https://0gbaltics.com/building-energy-efficiency" />
        <meta property="og:title" content={t('buildingEnergyPage.metaTitle')} />
        <meta property="og:description" content={t('buildingEnergyPage.metaDesc')} />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        
        {/* ========================================= */}
        {/* HERO SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="energy-hero">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('buildingEnergyPage.heroBadge')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {t('buildingEnergyPage.heroTitle')}
                </h1>
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  {t('buildingEnergyPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <Button 
                    size="lg"
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid="hero-cta"
                  >
                    {t('buildingEnergyPage.ctaButton')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
                
                {/* Benefit badges */}
                <div className="flex flex-wrap gap-3">
                  {['Energy Monitoring', 'HVAC Optimization', 'Occupancy Sensing', 'Analytics', 'Cost Reduction'].map((tech, i) => (
                    <span key={i} className="bg-slate-800 border border-slate-700 text-slate-300 text-sm px-3 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src={IMAGES.hero}
                  alt="Building Energy Efficiency - smart buildings with IoT sensors, energy meters, and management dashboard"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHY ENERGY EFFICIENCY MATTERS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="why-efficiency-matters">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('buildingEnergyPage.problemBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('buildingEnergyPage.problemTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('buildingEnergyPage.problemDesc')}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { stat: '40%', label: t('buildingEnergyPage.stat1') || 'of global energy consumed by buildings', color: 'bg-red-500' },
                  { stat: '30%', label: t('buildingEnergyPage.stat2') || 'typical energy waste in commercial buildings', color: 'bg-amber-500' },
                  { stat: '10-25%', label: t('buildingEnergyPage.stat3') || 'savings achievable with smart monitoring', color: 'bg-teal-600' },
                  { stat: '2-3 yrs', label: t('buildingEnergyPage.stat4') || 'typical payback period', color: 'bg-blue-600' },
                ].map((item, i) => (
                  <div key={i} className={`${item.color} p-6 rounded-lg text-white`}>
                    <div className="text-3xl font-bold mb-2">{item.stat}</div>
                    <p className="text-white/80 text-sm">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* THE PROBLEM: ENERGY INVISIBILITY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="energy-invisibility">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-red-400 mb-4">
                  {t('buildingEnergyPage.invisibilityBadge') || 'The Core Challenge'}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('buildingEnergyPage.invisibilityTitle') || 'Energy Consumption Is Often Invisible'}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('buildingEnergyPage.invisibilityDesc') || 'Many organizations do not have clear visibility into how their buildings actually consume energy.'}
                </p>
                
                <div className="space-y-3 mb-8">
                  {[
                    { icon: EyeOff, text: t('buildingEnergyPage.invisibility1') || 'Which equipment consumes the most energy' },
                    { icon: Clock, text: t('buildingEnergyPage.invisibility2') || 'When consumption peaks occur' },
                    { icon: AlertTriangle, text: t('buildingEnergyPage.invisibility3') || 'Where inefficiencies exist' },
                    { icon: TrendingUp, text: t('buildingEnergyPage.invisibility4') || 'Whether consumption is increasing' },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 bg-slate-800 p-3 rounded-lg">
                        <Icon className="h-5 w-5 text-red-400" />
                        <span className="text-slate-300">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="flex flex-col items-center gap-6">
                <div className="bg-slate-800 p-6 rounded-lg border border-slate-700 w-full">
                  <div className="flex items-center gap-3 mb-4">
                    <EyeOff className="h-8 w-8 text-red-400" />
                    <h4 className="text-white font-semibold">{t('buildingEnergyPage.withoutMonitoring') || 'Without Monitoring'}</h4>
                  </div>
                </div>
                <ArrowRight className="h-8 w-8 text-teal-400 rotate-90" />
                <div className="bg-teal-600/20 p-6 rounded-lg border border-teal-500/30 w-full">
                  <div className="flex items-center gap-3 mb-4">
                    <Eye className="h-8 w-8 text-teal-400" />
                    <h4 className="text-white font-semibold">{t('buildingEnergyPage.withMonitoring') || 'With IoT Monitoring'}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* IOT ENERGY MONITORING */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="iot-monitoring">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('buildingEnergyPage.iotMonitoringBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('buildingEnergyPage.iotMonitoringTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('buildingEnergyPage.iotMonitoringDesc')}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img 
                  src={IMAGES.energyFlow}
                  alt="Energy flow diagram in commercial building with monitoring points"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-6">{t('buildingEnergyPage.iotDevicesMeasure')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Zap, title: t('buildingEnergyPage.measureElectricity'), desc: t('buildingEnergyPage.measureElectricityDesc') },
                    { icon: Droplets, title: t('buildingEnergyPage.measureWater'), desc: t('buildingEnergyPage.measureWaterDesc') },
                    { icon: Thermometer, title: t('buildingEnergyPage.measureTemperature'), desc: t('buildingEnergyPage.measureTemperatureDesc') },
                    { icon: Droplets, title: t('buildingEnergyPage.measureHumidity'), desc: t('buildingEnergyPage.measureHumidityDesc') },
                    { icon: Users, title: t('buildingEnergyPage.measureOccupancy'), desc: t('buildingEnergyPage.measureOccupancyDesc') },
                    { icon: Settings, title: t('buildingEnergyPage.measureEquipment'), desc: t('buildingEnergyPage.measureEquipmentDesc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="bg-slate-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-5 w-5 text-teal-600" />
                          <h4 className="font-semibold text-slate-900 text-sm">{item.title}</h4>
                        </div>
                        <p className="text-slate-600 text-xs">{item.desc}</p>
                      </div>
                    );
                  })}
                </div>
                
                <p className="mt-6 text-slate-600">
                  {t('buildingEnergyPage.iotDataTransmission')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* ENERGY ANALYTICS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="energy-analytics">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('buildingEnergyPage.analyticsBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('buildingEnergyPage.analyticsTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('buildingEnergyPage.analyticsDesc')}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('buildingEnergyPage.typicalInsights')}</h4>
                <div className="space-y-3 mb-8">
                  {[
                    { icon: AlertTriangle, text: t('buildingEnergyPage.insight1'), color: 'text-red-500' },
                    { icon: Clock, text: t('buildingEnergyPage.insight2'), color: 'text-amber-500' },
                    { icon: Thermometer, text: t('buildingEnergyPage.insight3'), color: 'text-blue-500' },
                    { icon: Settings, text: t('buildingEnergyPage.insight4'), color: 'text-purple-500' },
                    { icon: TrendingUp, text: t('buildingEnergyPage.insight5'), color: 'text-teal-600' },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-200">
                        <Icon className={`h-5 w-5 ${item.color}`} />
                        <span className="text-slate-700 text-sm">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
                
                <p className="text-slate-600">
                  {t('buildingEnergyPage.analyticsConclusion')}
                </p>
              </div>
              
              <div>
                <img 
                  src={IMAGES.dashboard}
                  alt="Building energy analytics dashboard showing consumption graphs, alerts, and trends"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* HVAC OPTIMIZATION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="hvac-optimization">
          <div className="container-0g">
            {/* Section Header */}
            <div className="max-w-3xl mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                Largest Opportunity
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                HVAC Optimization
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('buildingEnergyPage.hvacDesc')}
              </p>
            </div>
            
            {/* Problem: Current Systems */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{t('buildingEnergyPage.hvacParadoxTitle')}</h3>
                  <p className="text-slate-700 mb-4">
                    {t('buildingEnergyPage.hvacParadoxDesc')}
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>{t('buildingEnergyPage.hvacProblem1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>{t('buildingEnergyPage.hvacProblem2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>{t('buildingEnergyPage.hvacProblem3')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>{t('buildingEnergyPage.hvacProblem4')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Solution: Harmony Device */}
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="bg-slate-200 rounded-xl p-8 flex items-center justify-center">
                  <img 
                    src="/assets/images/devices/harmony.webp"
                    alt="TEKTELIC Harmony smart thermostat with CO2 monitoring and HVAC control"
                    className="w-full max-w-md h-auto"
                  />
                </div>
              </div>
              
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-6">
                  <Zap className="h-4 w-4" />
                  <span className="text-sm font-medium">{t('buildingEnergyPage.harmonySolution')}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-6">
                  {t('buildingEnergyPage.harmonyTitle')}
                </h3>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('buildingEnergyPage.harmonyDesc')}
                </p>
                
                <div className="bg-teal-50 border border-teal-200 p-4 rounded-lg">
                  <p className="text-teal-800 text-sm">
                    {t('buildingEnergyPage.harmonyBenefit')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* OCCUPANCY-BASED ENERGY MANAGEMENT */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="occupancy-management">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('buildingEnergyPage.occupancyBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('buildingEnergyPage.occupancyTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('buildingEnergyPage.occupancyDesc')}
                </p>
                <p className="text-slate-400 mb-8 leading-relaxed">
                  {t('buildingEnergyPage.occupancyDesc2')}
                </p>
                
                <h4 className="font-semibold text-white mb-4">{t('buildingEnergyPage.highImpactApps')}</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    t('buildingEnergyPage.appMeetingRooms'),
                    t('buildingEnergyPage.appClassrooms'),
                    t('buildingEnergyPage.appIndividualOffices'),
                    t('buildingEnergyPage.appPublicBuildings'),
                    t('buildingEnergyPage.appCommonAreas'),
                    t('buildingEnergyPage.appParkingFacilities'),
                  ].map((space, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-800 p-3 rounded-lg">
                      <CheckCircle className="h-4 w-4 text-teal-400" />
                      <span className="text-slate-300 text-sm">{space}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <img 
                  src={IMAGES.occupancy}
                  alt="Occupancy-based energy management showing office floor plan with occupied and unoccupied zones"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SMART BUILDING SENSORS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="building-sensors">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('buildingEnergyPage.sensorsBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('buildingEnergyPage.sensorsTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('buildingEnergyPage.sensorsDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {[
                {
                  icon: Thermometer,
                  title: t('buildingEnergyPage.sensorTemp'),
                  desc: t('buildingEnergyPage.sensorTempDesc'),
                  color: 'bg-red-500'
                },
                {
                  icon: Droplets,
                  title: t('buildingEnergyPage.sensorHumidity'),
                  desc: t('buildingEnergyPage.sensorHumidityDesc'),
                  color: 'bg-blue-500'
                },
                {
                  icon: Wind,
                  title: t('buildingEnergyPage.sensorCO2'),
                  desc: t('buildingEnergyPage.sensorCO2Desc'),
                  color: 'bg-teal-600'
                },
                {
                  icon: Users,
                  title: t('buildingEnergyPage.sensorOccupancy'),
                  desc: t('buildingEnergyPage.sensorOccupancyDesc'),
                  color: 'bg-purple-600'
                },
                {
                  icon: Zap,
                  title: t('buildingEnergyPage.sensorEnergy'),
                  desc: t('buildingEnergyPage.sensorEnergyDesc'),
                  color: 'bg-amber-500'
                },
                {
                  icon: Droplets,
                  title: t('buildingEnergyPage.sensorWater'),
                  desc: t('buildingEnergyPage.sensorWaterDesc'),
                  color: 'bg-cyan-500'
                },
              ].map((sensor, i) => {
                const Icon = sensor.icon;
                return (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                    <div className={`${sensor.color} p-4 flex items-center gap-3`}>
                      <Icon className="h-6 w-6 text-white" />
                      <h3 className="text-white font-semibold">{sensor.title}</h3>
                    </div>
                    <div className="p-6">
                      <p className="text-slate-600 text-sm leading-relaxed">{sensor.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center">
              <img 
                src={IMAGES.sensors}
                alt="Smart building sensors - temperature, humidity, CO2, occupancy, electricity, water"
                className="w-full max-w-md h-auto"
              />
            </div>
            
            <div className="mt-8 text-center">
              <Link 
                to="/indoor-air-quality-monitoring"
                className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700"
              >
                {t('buildingEnergyPage.learnMoreIAQ')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* ENERGY EFFICIENCY STANDARDS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="energy-standards">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('buildingEnergyPage.standardsBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('buildingEnergyPage.standardsTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('buildingEnergyPage.standardsDesc')}
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="bg-white p-6 rounded-lg border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-2">{t('buildingEnergyPage.epbdTitle')}</h4>
                    <p className="text-slate-600 text-sm">
                      {t('buildingEnergyPage.epbdDesc')}
                    </p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-2">{t('buildingEnergyPage.epcTitle')}</h4>
                    <p className="text-slate-600 text-sm">
                      {t('buildingEnergyPage.epcDesc')}
                    </p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-2">{t('buildingEnergyPage.esgTitle')}</h4>
                    <p className="text-slate-600 text-sm">
                      {t('buildingEnergyPage.esgDesc')}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-teal-600 p-8 rounded-lg text-white">
                <h4 className="font-semibold mb-6 text-xl">{t('buildingEnergyPage.monitoringSupports')}</h4>
                <div className="space-y-4">
                  {[
                    t('buildingEnergyPage.support1'),
                    t('buildingEnergyPage.support2'),
                    t('buildingEnergyPage.support3'),
                    t('buildingEnergyPage.support4'),
                    t('buildingEnergyPage.support5'),
                    t('buildingEnergyPage.support6'),
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-teal-200" />
                      <span className="text-teal-50">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* ROI OF ENERGY MONITORING */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="energy-roi">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('buildingEnergyPage.roiBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('buildingEnergyPage.roiTitle')}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {t('buildingEnergyPage.roiDesc')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-slate-50 p-8 rounded-lg border border-slate-200">
                  <TrendingDown className="h-10 w-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('buildingEnergyPage.typicalBenefits')}</h3>
                  <ul className="space-y-3">
                    {[
                      t('buildingEnergyPage.benefit1'),
                      t('buildingEnergyPage.benefit2'),
                      t('buildingEnergyPage.benefit3'),
                      t('buildingEnergyPage.benefit4'),
                      t('buildingEnergyPage.benefit5'),
                      t('buildingEnergyPage.benefit6'),
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <CheckCircle className="h-4 w-4 text-teal-600" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-slate-900 p-8 rounded-lg text-white">
                  <DollarSign className="h-10 w-10 text-teal-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-4">{t('buildingEnergyPage.investmentTitle')}</h3>
                  <ul className="space-y-3">
                    {[
                      t('buildingEnergyPage.investment1'),
                      t('buildingEnergyPage.investment2'),
                      t('buildingEnergyPage.investment3'),
                      t('buildingEnergyPage.investment4'),
                      t('buildingEnergyPage.investment5'),
                      t('buildingEnergyPage.investment6'),
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-300">
                        <CheckCircle className="h-4 w-4 text-teal-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="bg-teal-50 border border-teal-200 p-6 rounded-lg text-center">
                <p className="text-teal-800">
                  <strong>{t('buildingEnergyPage.roiNote')}</strong>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHY 0G BALTICS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="why-0g-baltics">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('buildingEnergyPage.whyBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('buildingEnergyPage.whyTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('buildingEnergyPage.whyDesc')}
                </p>
                
                <div className="space-y-4">
                  {[
                    { title: t('buildingEnergyPage.whyFeature1'), desc: t('buildingEnergyPage.whyFeature1Desc') },
                    { title: t('buildingEnergyPage.whyFeature2'), desc: t('buildingEnergyPage.whyFeature2Desc') },
                    { title: t('buildingEnergyPage.whyFeature3'), desc: t('buildingEnergyPage.whyFeature3Desc') },
                    { title: t('buildingEnergyPage.whyFeature4'), desc: t('buildingEnergyPage.whyFeature4Desc') },
                    { title: t('buildingEnergyPage.whyFeature5'), desc: t('buildingEnergyPage.whyFeature5Desc') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <CheckCircle className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-white">{item.title}</h4>
                        <p className="text-slate-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-slate-800 p-8 rounded-lg border border-slate-700">
                  <h4 className="text-white font-semibold mb-6 text-lg">{t('buildingEnergyPage.relatedSolutions')}</h4>
                  <div className="space-y-4">
                    {[
                      { to: '/indoor-air-quality-monitoring', label: t('buildingEnergyPage.relatedIAQ'), desc: t('buildingEnergyPage.relatedIAQDesc') },
                      { to: '/smart-city-iot-infrastructure', label: t('buildingEnergyPage.relatedSmartCity'), desc: t('buildingEnergyPage.relatedSmartCityDesc') },
                      { to: '/connectivity', label: t('buildingEnergyPage.relatedConnectivity'), desc: t('buildingEnergyPage.relatedConnectivityDesc') },
                      { to: '/smart-metering', label: t('buildingEnergyPage.relatedMetering'), desc: t('buildingEnergyPage.relatedMeteringDesc') },
                    ].map((link, i) => (
                      <Link key={i} to={link.to} className="block bg-slate-700 p-4 rounded-lg hover:bg-slate-600 transition-colors group">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="text-white font-medium">{link.label}</h5>
                            <p className="text-slate-400 text-sm">{link.desc}</p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-teal-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CTA SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-teal-600" data-testid="cta-section">
          <div className="container-0g text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('buildingEnergyPage.ctaTitle')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
              {t('buildingEnergyPage.ctaDesc')}
            </p>
            <Button 
              size="lg"
              className="bg-white text-teal-700 hover:bg-teal-50 font-semibold"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="cta-button"
            >
              {t('buildingEnergyPage.ctaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* ========================================= */}
        {/* CONTACT FORM */}
        {/* ========================================= */}
        <div id="contact-form">
          <ContactForm />
        </div>

      </main>

      <Footer />
    </>
  );
};

export default BuildingEnergyEfficiencyPage;
